import { Box, Divider, Stack, Typography } from "@mui/material";
import { IconWrapper } from "components/ui/IconWrapper";
import { CloseInEmptyCircleIcon } from "../../../images";
import { TextBlock } from "./TextBlock";
import { useEffect } from "react";

const doDont = {
  do: {
    title: "DO",
    description: "What successful SDRs always do",
    icon: (
      <IconWrapper width={"22px"} height={"22px"}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.4471 8.25L9.98618 12.8672L7.55602 10.3589L6.60156 11.3468L9.98618 14.85L15.4016 9.2449L14.4471 8.25Z"
            fill="#27AE60"
          />
          <path
            d="M10.999 1.83398C16.0589 1.83416 20.165 5.94108 20.165 11.001C20.1649 16.0607 16.0588 20.1668 10.999 20.167C5.93913 20.167 1.83221 16.0608 1.83203 11.001C1.83203 5.94098 5.93902 1.83398 10.999 1.83398ZM10.999 3.66699C6.94736 3.66699 3.66504 6.94931 3.66504 11.001C3.66521 15.0525 6.94746 18.334 10.999 18.334C15.0504 18.3338 18.3319 15.0524 18.332 11.001C18.332 6.94942 15.0505 3.66717 10.999 3.66699Z"
            fill="#7DCEA0"
          />
          <path
            d="M10.999 1.83398C16.0589 1.83416 20.165 5.94108 20.165 11.001C20.1649 16.0607 16.0588 20.1668 10.999 20.167C5.93913 20.167 1.83221 16.0608 1.83203 11.001C1.83203 5.94098 5.93902 1.83398 10.999 1.83398ZM10.999 3.66699C6.94736 3.66699 3.66504 6.94931 3.66504 11.001C3.66521 15.0525 6.94746 18.334 10.999 18.334C15.0504 18.3338 18.3319 15.0524 18.332 11.001C18.332 6.94942 15.0505 3.66717 10.999 3.66699Z"
            stroke="white"
          />
        </svg>
      </IconWrapper>
    ),
    list: [
      "Personalize with prospect intelligence.",
      "Ask discovery questions that build trust.",
      "Position the solution as a strategic resource.",
      "Prospect with industry insights, NOT features.",
      "Follow up thoughtfully with value.",
    ],
  },
  dont: {
    title: "DON’T",
    description: "Mistakes that kill engagement",
    icon: <CloseInEmptyCircleIcon width={"22px"} height={"22px"} />,
    list: [
      "Assume they are ready to buy.",
      "Send generic, templated outreach.",
      "Talk at them - ask, listen, learn.",
      "Push a meeting too soon.",
      "Forget to log discovery in CRM.",
    ],
  },
};

const Block = ({ field }: { field: keyof typeof doDont }) => {
  return (
    <Stack gap={"12px"} flexGrow={1}>
      <Stack flexDirection={"row"} gap={"12px"} alignItems="flex-start">
        {doDont[field].icon}
        <Stack>
          <Typography
            sx={{
              fontSize: "16px",
              lineHeight: "22px",
              fontWeight: 600,
              color: field === "do" ? "#27AE60" : "#C0392B",
            }}
          >
            {doDont[field].title}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "22px",
              color: "#797979",
            }}
          >
            {doDont[field].description}
          </Typography>
        </Stack>
      </Stack>
      <Stack gap="8px" paddingLeft={"8px"}>
        {doDont[field].list.map((el) => (
          <Stack gap="8px" flexDirection="row" alignItems="flex-start" key={el}>
            <Stack
              alignItems="center"
              justifyContent="center"
              paddingTop={"10px"}
            >
              <Box
                style={{
                  minWidth: "6px",
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#005EAB",
                }}
              />
            </Stack>
            <TextBlock text={[el]} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export const DoDontBlock = ({
  setTopic,
}: {
  setTopic: (topic: string) => void;
}) => {
  const topic = "Do’s and Don'ts for Engaging Intent-Based Prospects";

  useEffect(() => {
    setTopic(topic);
  }, [topic]);

  return (
    <Stack
      id={topic}
      sx={{
        position: "relative",
        padding: "32px 24px",
        borderRadius: "16px",
        gap: "24px",
        backgroundColor: "#fff",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          padding: "1px",
          background: "linear-gradient(248.72deg, #1C3C6C -0.33%, #A1C7FF 33%)",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
      }}
    >
      <Stack gap="24px">
        <Typography
          component="h4"
          sx={{
            fontSize: "22px",
            lineHeight: "28px",
            fontWeight: 600,
            color: "#005EAB",
          }}
        >
          {topic}
        </Typography>
        <Stack flexDirection={"row"} gap="32px" flexGrow={1}>
          <Block field={"do"} />
          <Divider orientation="vertical" flexItem />
          <Block field={"dont"} />
        </Stack>
      </Stack>
    </Stack>
  );
};
